import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { InvokePanel } from '../ui/InvokePanel';
import { BodyGalleryPanel } from '../ui/BodyGalleryPanel';
import { WeaponGalleryPanel } from '../ui/WeaponGalleryPanel';
import { CharFrameDebugPanel, type LocalPlayerSpriteKeys } from '../ui/CharFrameDebugPanel';
import { ItemPanel } from '../ui/ItemPanel';
import { ClientMessageType } from '@shared/types/messages';

/**
 * Paneles de dev/debug toggleables desde la barra del menú (⚙): listado de invocación (📜), galería
 * de bodies (🧍), galería de armas (⚔️) y stepper de frames del personaje (🎞️), más los comandos de
 * spawn/override que disparan. Extraído de GameScene (god-object). El estado (invokePanel/
 * bodyGalleryPanel/…) vive en GameScene (host) y se accede vía `this.scene.*`.
 */
export class DevPanelsController {
  constructor(private scene: GameScene) {}

  public openInvokePanel(): void {
    if (!this.scene.invokePanel) {
      this.createInvokePanel();
    }

    this.scene.invokeIsOpen = true;
    this.scene.invokePanel?.open();

    // Pedir la lista cada vez: evita cache stale cuando se agregan/quitan
    // templates en shared/data/npcs.ts sin reload completo del cliente.
    this.scene.invoke.requestNpcTemplates();
    if (this.scene.invokeNpcTemplates.length > 0) {
      this.scene.invoke.refreshInvokePanelContents();
    }
  }

  public closeInvokePanel(): void {
    this.scene.invokeIsOpen = false;
    this.scene.invokePanel?.close();
  }

  public toggleInvokePanel(): void {
    if (!this.scene.invokePanel) {
      this.createInvokePanel();
    }
    this.scene.invokeIsOpen = !this.scene.invokeIsOpen;
    if (this.scene.invokeIsOpen) {
      this.scene.invokePanel?.open();
      // Siempre re-pedir al abrir (los templates pueden haber cambiado);
      // la lista anterior se sigue mostrando hasta que llega la respuesta.
      this.scene.invoke.requestNpcTemplates();
      if (this.scene.invokeNpcTemplates.length > 0) {
        this.scene.invoke.refreshInvokePanelContents();
      }
    } else {
      this.scene.invokePanel?.close();
    }
  }

  public createInvokePanel(): void {
    const { width, height } = this.scene.scale;
    const savedX = this.scene.persistence.loadClientOptions()?.invokePanelX ?? width / 2;
    const savedY = this.scene.persistence.loadClientOptions()?.invokePanelY ?? height / 2;

    this.scene.invokePanel = new InvokePanel(
      this.scene,
      {
        onSelect: (index) => this.scene.invoke.handleInvokeListClick(index),
        onInvoke: () => this.scene.invoke.sendInvokeNpcCommand(),
        onMove: (x, y) => this.scene.persistence.savePanelPosition('invoke', x, y),
      },
      {
        initialX: savedX,
        initialY: savedY,
        width: 650,
        height: 380,  // antes 260; sube para acomodar lista de 24 filas (más templates futuros).
      }
    );
  }

  public createItemPanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = savedOptions?.itemPanelX ?? width / 2;
    const panelY = savedOptions?.itemPanelY ?? height / 2;

    this.scene.itemPanel = new ItemPanel(
      this.scene,
      {
        onMove: (x, y) => this.scene.persistence.savePanelPosition('item', x, y),
        onSpawnItem: (itemId) => this.sendSpawnItemTemplateCommand(itemId),
      },
      { initialX: panelX, initialY: panelY }
    );
  }

  public sendSpawnItemTemplateCommand(itemId?: string): void {
    if (!this.scene.colyseusClient || !this.scene.itemPanel) return;
    const templates = this.scene.itemPanel.getTemplates();
    const selectedIndex = templates.findIndex((t) => t.id === itemId);
    if (selectedIndex < 0 || selectedIndex >= templates.length) return;
    const selected = templates[selectedIndex];
    this.scene.colyseusClient.send({
      type: ClientMessageType.SPAWN_ITEM_TEMPLATE,
      itemId: selected.id,
    } as never);
  }

  // -----------------
  // Panel de galería de bodies (preview local)
  // -----------------

  public toggleBodyGalleryPanel(): void {
    if (!this.scene.bodyGalleryPanel) {
      this.createBodyGalleryPanel();
    }
    this.scene.bodyGalleryPanel.toggle();
  }

  public openBodyGalleryPanel(): void {
    if (!this.scene.bodyGalleryPanel) {
      this.createBodyGalleryPanel();
    }
    this.scene.bodyGalleryPanel.open();
  }

  public closeBodyGalleryPanel(): void {
    this.scene.bodyGalleryPanel?.close();
  }

  public createBodyGalleryPanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = savedOptions?.bodyGalleryPanelX ?? width / 2;
    const panelY = savedOptions?.bodyGalleryPanelY ?? height / 2;

    this.scene.bodyGalleryPanel = new BodyGalleryPanel(
      this.scene,
      {
        onMove: (x, y) => this.scene.persistence.savePanelPosition('bodyGallery', x, y),
        getLocalPlayerHeadId: () => this.scene.invoke.getLocalPlayerHeadIdForPreview(),
        onApplyBody: (bodyId) => this.sendSetBodyOverrideCommand(bodyId),
        onToggleHeadHidden: (hidden) => this.sendSetHeadHiddenCommand(hidden),
      },
      {
        initialX: panelX,
        initialY: panelY,
        BODY_FRAME_HEIGHT: GameScene.BODY_FRAME_HEIGHT,
        HEAD_OFFSET_FROM_BODY_BOTTOM: GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM,
        HEAD_FRAME_HEIGHT: GameScene.HEAD_FRAME_HEIGHT,
      }
    );
  }

  public sendSetBodyOverrideCommand(bodyId: number): void {
    if (!this.scene.colyseusClient) return;
    this.scene.colyseusClient.send({
      type: ClientMessageType.SET_BODY_OVERRIDE,
      bodyId,
    } as never);
  }

  public sendSetHeadHiddenCommand(hidden: boolean): void {
    if (!this.scene.colyseusClient) return;
    this.scene.colyseusClient.send({
      type: ClientMessageType.SET_HEAD_HIDDEN,
      hidden,
    } as never);
  }

  // -----------------
  // Panel de galería de weapons (preview local, no equipa)
  // -----------------

  public toggleWeaponGalleryPanel(): void {
    if (!this.scene.weaponGalleryPanel) {
      this.createWeaponGalleryPanel();
    }
    this.scene.weaponGalleryPanel?.toggle();
  }

  /**
   * Preview client-side del arma sobre el player local (lo dispara la galería de
   * weapons). NO manda nada al server: sólo cambia el sheet del sprite local.
   * `assetName` null/'' = quitar override y volver al arma real del server.
   */
  previewLocalWeapon(assetName: string | null): void {
    this.scene.localWeaponPreviewSheetKey =
      assetName && assetName.length > 0 ? `gwpreview_${assetName}` : null;
  }

  public createWeaponGalleryPanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = savedOptions?.weaponGalleryPanelX ?? width / 2;
    const panelY = savedOptions?.weaponGalleryPanelY ?? height / 2;

    this.scene.weaponGalleryPanel = new WeaponGalleryPanel(
      this.scene,
      {
        onMove: (x, y) => this.scene.persistence.savePanelPosition('weaponGallery', x, y),
        getLocalPlayerBodyId: () => this.scene.invoke.getLocalPlayerBodyIdForPreview(),
        getLocalPlayerHeadId: () => this.scene.invoke.getLocalPlayerHeadIdForPreview(),
        onSelectWeapon: (name) => this.previewLocalWeapon(name),
      },
      {
        initialX: panelX,
        initialY: panelY,
        BODY_FRAME_HEIGHT: GameScene.BODY_FRAME_HEIGHT,
        HEAD_OFFSET_FROM_BODY_BOTTOM: GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM,
        HEAD_FRAME_HEIGHT: GameScene.HEAD_FRAME_HEIGHT,
        WEAPON_OFFSET_X: GameScene.WEAPON_OFFSET_X,
        WEAPON_OFFSET_Y: GameScene.WEAPON_OFFSET_Y,
        WEAPON_LATERAL_DX: GameScene.WEAPON_LATERAL_DX,
      }
    );
  }

  // -----------------
  // Panel de debug de frames del personaje (stepper de cuadros por parte)
  // -----------------

  public toggleCharFrameDebugPanel(): void {
    if (!this.scene.charFrameDebugPanel) {
      this.createCharFrameDebugPanel();
    }
    this.scene.charFrameDebugPanel?.toggle();
  }

  public createCharFrameDebugPanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = savedOptions?.charFramePanelX ?? width / 2;
    const panelY = savedOptions?.charFramePanelY ?? height / 2;

    this.scene.charFrameDebugPanel = new CharFrameDebugPanel(
      this.scene,
      {
        onMove: (x, y) => this.scene.persistence.savePanelPosition('charFrame', x, y),
        getLocalPlayerSpriteKeys: () => this.getLocalPlayerSpriteKeysForDebug(),
      },
      {
        initialX: panelX,
        initialY: panelY,
        HEAD_OFFSET_FROM_BODY_BOTTOM: GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM,
        HEAD_FRAME_HEIGHT: GameScene.HEAD_FRAME_HEIGHT,
        WEAPON_OFFSET_X: GameScene.WEAPON_OFFSET_X,
        WEAPON_OFFSET_Y: GameScene.WEAPON_OFFSET_Y,
        WEAPON_LATERAL_DX: GameScene.WEAPON_LATERAL_DX,
        SHIELD_OFFSET_X: GameScene.SHIELD_OFFSET_X,
      }
    );
  }

  /**
   * Snapshot de las texture keys ACTUALES de cada capa del player local, leídas
   * de los sprites reales del render (captura overrides y previews activos).
   * Para el panel de debug de frames.
   */
  public getLocalPlayerSpriteKeysForDebug(): LocalPlayerSpriteKeys {
    const empty: LocalPlayerSpriteKeys = {
      bodyId: 0,
      bodyKey: null,
      headKey: null,
      weaponKey: null,
      shieldKey: null,
      helmKey: null,
      bootKey: null,
    };
    if (!this.scene.localSessionId) return empty;
    const render = this.scene.playerObjects.get(this.scene.localSessionId);
    if (!render) return empty;
    return {
      bodyId: this.scene.invoke.getLocalPlayerBodyIdForPreview(),
      bodyKey: render.sprite?.texture?.key ?? null,
      headKey: render.head?.texture?.key ?? null,
      weaponKey: render.weapon?.texture?.key ?? null,
      shieldKey: render.shield?.texture?.key ?? null,
      helmKey: render.helm?.texture?.key ?? null,
      bootKey: render.boots?.texture?.key ?? null,
    };
  }
}
